import { Link, router } from 'expo-router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import { FacebookButton, GoogleButton } from '@/src/components/FacebookButton';
import { ErrorView } from '@/src/components/ErrorView';
import { Input } from '@/src/components/Input';
import { Screen } from '@/src/components/Screen';
import { useToast } from '@/src/components/Toast';
import { requestAccountDeletion } from '@/src/features/account-deletion/account-deletion-service';
import {
  emailChangeSchema,
  profileSchema,
  type EmailChangeValues,
  type ProfileValues,
} from '@/src/features/auth/validation';
import { useFacebookAuthRequest } from '@/src/features/auth/use-facebook-auth';
import { useGoogleAuthRequest } from '@/src/features/auth/use-google-auth';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_VERSION,
} from '@/src/features/legal/legal-documents';
import {
  defaultProfileFormValues,
  genderOptions,
  profileGenderToFormValue,
  genderValueToFormValue,
} from '@/src/features/profile/profile-form';
import { CityStateAutocomplete } from '@/src/features/profile/CityStateAutocomplete';
import {
  defaultNotificationSettings,
  defaultWritingAssistSettings,
  MAX_PROFILE_BIO_LENGTH,
  unblockUser as unblockProfileUser,
  type NotificationSettings,
  type ProfileConnection,
  type UpdateProfileInput,
  type WritingAssistSettings,
} from '@/src/features/profile/profile-service';
import {
  fullNameIsIdentityLocked,
  parseVerifiedAccount,
} from '@/src/features/verification/verification-status';
import { WebPushSettingsPanel } from '@/src/features/pwa/WebPushSettingsPanel';
import {
  useBlockedUsers,
  useProfile,
  useUpdateProfile,
} from '@/src/features/profile/use-profile';
import {
  formatUsPhoneNumberDisplay,
  formatUsPhoneNumberInput,
  normalizeUsPhoneNumber,
  phoneNumberMatchesStoredPhone,
} from '@/src/features/verification/phone-number';
import {
  confirmPhoneVerificationCode,
  sendPhoneVerificationCode,
} from '@/src/features/verification/verification-service';
import { PhoneVerificationCodeModal } from '@/src/features/verification/PhoneVerificationCodeModal';
import { trackEvent } from '@/src/lib/analytics';
import { submitSentryFeedback } from '@/src/lib/sentry';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { copyTextToClipboard } from '@/src/utils/clipboard';
import { isShareCancelError } from '@/src/utils/errors';

type NotificationChannel = 'email' | 'inApp' | 'push';
type NotificationTopic =
  | 'likes'
  | 'comments'
  | 'follows'
  | 'invites'
  | 'garage'
  | 'mentions'
  | 'commentReactions'
  | 'platformUpdates';

const notificationRows: { key: NotificationTopic; label: string }[] = [
  { key: 'likes', label: 'Post reactions' },
  { key: 'comments', label: 'Comments' },
  { key: 'commentReactions', label: 'Comment reactions' },
  { key: 'follows', label: 'Follows' },
  { key: 'invites', label: 'Invites' },
  { key: 'garage', label: 'Garage activity' },
  { key: 'mentions', label: 'Mentions' },
  { key: 'platformUpdates', label: 'Daily Frienzzi updates' },
];
const supportEmail = 'support@frienzzi.com';

export default function SettingsScreen() {
  const {
    connectFacebook,
    connectGoogle,
    requestEmailChange,
    sendPasswordReset,
    signOut,
    user,
  } = useAuth();
  const { showError, showSuccess } = useToast();
  const facebookAuth = useFacebookAuthRequest();
  const googleAuth = useGoogleAuthRequest();
  const providerIds = user?.providerData.map((provider) => provider.providerId);
  const googleConnected = providerIds?.includes('google.com') ?? false;
  const profileQuery = useProfile(user?.uid);
  const [blockedUsersVisible, setBlockedUsersVisible] = useState(false);
  const [blockedSearchInput, setBlockedSearchInput] = useState('');
  const [blockedSearchQuery, setBlockedSearchQuery] = useState('');
  const blockedUsersQuery = useBlockedUsers(user?.uid, {
    enabled: blockedUsersVisible,
    query: blockedSearchQuery,
  });
  const updateProfile = useUpdateProfile(user?.uid);
  const [signingOut, setSigningOut] = useState(false);
  const [unblockingUid, setUnblockingUid] = useState<string | null>(null);
  const [connectingFacebook, setConnectingFacebook] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);
  const [notificationSettings, setNotificationSettings] =
    useState<NotificationSettings>(() => defaultNotificationSettings());
  const [writingAssist, setWritingAssist] = useState<WritingAssistSettings>(
    () => defaultWritingAssistSettings(),
  );
  const [savingNotificationSetting, setSavingNotificationSetting] = useState<
    string | null
  >(null);
  const [savingWritingAssist, setSavingWritingAssist] = useState(false);
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [feedRankingVisible, setFeedRankingVisible] = useState(false);
  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const {
    control,
    formState: { dirtyFields, errors, isSubmitting },
    handleSubmit,
    setError,
    setValue,
  } = useForm<ProfileValues>({
    defaultValues: defaultProfileFormValues(),
  });
  const {
    control: emailChangeControl,
    formState: {
      errors: emailChangeErrors,
      isSubmitting: isSubmittingEmailChange,
    },
    handleSubmit: handleEmailChangeSubmit,
    reset: resetEmailChange,
    setError: setEmailChangeError,
  } = useForm<EmailChangeValues>({
    defaultValues: {
      currentPassword: '',
      newEmail: '',
    },
  });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneVerificationId, setPhoneVerificationId] = useState<string | null>(
    null,
  );
  const [phoneCodeModalVisible, setPhoneCodeModalVisible] = useState(false);
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [confirmingPhoneCode, setConfirmingPhoneCode] = useState(false);

  useEffect(() => {
    const profile = profileQuery.data;

    if (!profile) {
      return;
    }

    setValue('fullName', profile.fullName ?? '');
    setValue('bio', profile.bio);
    setValue('gender', profileGenderToFormValue(profile.gender));
    setValue('location', profile.location ?? null);
    setNotificationSettings(
      profile.notificationSettings ?? defaultNotificationSettings(),
    );
    setWritingAssist(profile.writingAssist ?? defaultWritingAssistSettings());
  }, [profileQuery.data, setValue]);

  useEffect(() => {
    if (!blockedUsersVisible) {
      setBlockedSearchInput('');
      setBlockedSearchQuery('');
      return;
    }

    const timeout = setTimeout(() => {
      setBlockedSearchQuery(blockedSearchInput.trim());
    }, 250);

    return () => clearTimeout(timeout);
  }, [blockedSearchInput, blockedUsersVisible]);

  useEffect(() => {
    if (phoneVerificationId) {
      return;
    }

    setPhoneNumber(
      formatUsPhoneNumberDisplay(
        profileQuery.data?.verification?.phone?.phoneNumber,
      ),
    );
  }, [phoneVerificationId, profileQuery.data?.verification?.phone?.phoneNumber]);

  async function handleSignOut() {
    setSigningOut(true);

    try {
      await signOut();
      router.replace('/sign-in');
    } catch (caughtError) {
      showError(caughtError, 'Unable to sign out. Please try again.');
    } finally {
      setSigningOut(false);
    }
  }

  async function handleConnectFacebook() {
    setConnectingFacebook(true);

    try {
      const accessToken = await facebookAuth.promptForAccessToken();
      await connectFacebook(accessToken);
      await profileQuery.refetch();
      showSuccess('Facebook is connected to your account.', 'Facebook linked');
    } catch (caughtError) {
      showError(caughtError, 'Unable to connect Facebook.');
    } finally {
      setConnectingFacebook(false);
    }
  }

  async function handleConnectGoogle() {
    setConnectingGoogle(true);

    try {
      const tokens = await googleAuth.promptForCredentialTokens();
      await connectGoogle(tokens);
      showSuccess('Google is connected to your account.', 'Google linked');
    } catch (caughtError) {
      showError(caughtError, 'Unable to connect Google.');
    } finally {
      setConnectingGoogle(false);
    }
  }

  async function handleRequestDeletion() {
    setRequestingDeletion(true);

    try {
      await requestAccountDeletion();
      showSuccess(
        'Your account deletion request was scheduled.',
        'Deletion scheduled',
      );
      router.replace('/account-deletion-pending');
    } catch (caughtError) {
      showError(caughtError, 'Unable to request account deletion.');
    } finally {
      setRequestingDeletion(false);
    }
  }

  async function handlePasswordReset() {
    if (!user?.email) {
      showError(
        new Error('This account does not have an email address.'),
        'Unable to send password reset.',
      );
      return;
    }

    try {
      await sendPasswordReset(user.email);
      showSuccess('Check your email for a password reset link.', 'Email sent');
    } catch (caughtError) {
      showError(caughtError, 'Unable to send a password reset email.');
    }
  }

  const handleEmailChange = handleEmailChangeSubmit(async (values) => {
    const parsed = emailChangeSchema.safeParse(values);

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0];

        if (field === 'newEmail' || field === 'currentPassword') {
          setEmailChangeError(field, { message: issue.message });
        }
      });
      return;
    }

    if (
      user?.email &&
      parsed.data.newEmail.toLowerCase() === user.email.toLowerCase()
    ) {
      setEmailChangeError('newEmail', {
        message: 'Enter a different email address.',
      });
      return;
    }

    try {
      await requestEmailChange(
        parsed.data.newEmail,
        parsed.data.currentPassword,
      );
      resetEmailChange();
      trackEvent('email_change_request');
      showSuccess(
        'Check your new email address for a confirmation link.',
        'Email change sent',
      );
    } catch (caughtError) {
      showError(caughtError, 'Unable to request that email change.');
    }
  });

  function handlePhoneNumberChange(value: string) {
    setPhoneNumber(formatUsPhoneNumberInput(value));
    setPhoneVerificationCode('');
    setPhoneVerificationId(null);
    setPhoneCodeModalVisible(false);
  }

  async function handleSendPhoneChangeCode() {
    if (!normalizedPhoneNumber) {
      showError(
        new Error('Enter a valid US cell phone number.'),
        'Unable to send phone verification code.',
      );
      return;
    }

    if (phoneChangeMatchesCurrentPhone) {
      showError(
        new Error('Enter a different phone number.'),
        'Unable to send phone verification code.',
      );
      return;
    }

    setSendingPhoneCode(true);

    try {
      const result = await sendPhoneVerificationCode(phoneNumber);

      if (result.handoff) {
        trackEvent('phone_change_handoff_open');
        showSuccess(
          'Complete phone verification in the browser window that opened.',
          'Phone verification opened',
        );
        return;
      }

      setPhoneVerificationId(result.verificationId);
      setPhoneCodeModalVisible(true);
      trackEvent('phone_change_code_send');
      showSuccess(
        'Enter the SMS code sent to your new phone number.',
        'Code sent',
      );
    } catch (caughtError) {
      showError(caughtError, 'Unable to send phone verification code.');
    } finally {
      setSendingPhoneCode(false);
    }
  }

  async function handleConfirmPhoneChangeCode() {
    if (!phoneVerificationId) {
      return;
    }

    setConfirmingPhoneCode(true);

    try {
      await confirmPhoneVerificationCode({
        code: phoneVerificationCode,
        verificationId: phoneVerificationId,
      });
      setPhoneVerificationCode('');
      setPhoneVerificationId(null);
      setPhoneCodeModalVisible(false);
      await profileQuery.refetch();
      trackEvent('phone_change_confirm');
      showSuccess('Your verified phone number was updated.', 'Phone updated');
    } catch (caughtError) {
      showError(caughtError, 'Unable to verify that phone code.');
    } finally {
      setConfirmingPhoneCode(false);
    }
  }

  async function handleNotificationSettingChange({
    channel,
    topic,
    value,
  }: {
    channel: NotificationChannel;
    topic: NotificationTopic;
    value: boolean;
  }) {
    const previousSettings = notificationSettings;
    const nextSettings: NotificationSettings = {
      ...notificationSettings,
      [channel]: {
        ...notificationSettings[channel],
        [topic]: value,
      },
      likeEmailDigestCadence: 'hourly',
      platformUpdateDigestCadence: 'daily',
    };
    const settingKey = `${channel}.${topic}`;

    setNotificationSettings(nextSettings);
    setSavingNotificationSetting(settingKey);

    try {
      const result = await updateProfile.mutateAsync({
        notificationSettings: nextSettings,
      });
      await profileQuery.refetch();
      trackEvent('notification_setting_update', {
        channel,
        enabled: value,
        topic,
      });
      showSuccess(
        result === 'queued'
          ? 'Your notification preference will sync when your connection is back.'
          : 'Your notification preference was saved.',
        result === 'queued' ? 'Preference queued' : 'Preference saved',
      );
    } catch (caughtError) {
      setNotificationSettings(previousSettings);
      showError(caughtError, 'Unable to save notification preferences.');
    } finally {
      setSavingNotificationSetting(null);
    }
  }

  async function handleWritingAssistChange(value: boolean) {
    const previousSettings = writingAssist;
    const nextSettings: WritingAssistSettings = {
      ...writingAssist,
      autoCorrectEnabled: value,
    };

    setWritingAssist(nextSettings);
    setSavingWritingAssist(true);

    try {
      const result = await updateProfile.mutateAsync({
        writingAssist: nextSettings,
      });
      await profileQuery.refetch();
      trackEvent('writing_assist_setting_update', {
        enabled: value,
      });
      showSuccess(
        result === 'queued'
          ? 'Your writing assist preference will sync when your connection is back.'
          : 'Your writing assist preference was saved.',
        result === 'queued' ? 'Preference queued' : 'Preference saved',
      );
    } catch (caughtError) {
      setWritingAssist(previousSettings);
      showError(caughtError, 'Unable to save writing assist preference.');
    } finally {
      setSavingWritingAssist(false);
    }
  }

  async function handleUnblockUser(profile: ProfileConnection) {
    if (unblockingUid) {
      return;
    }

    setUnblockingUid(profile.uid);

    try {
      await unblockProfileUser(profile.uid);
      await blockedUsersQuery.refetch();
      showSuccess(`${profile.fullName} is unblocked.`, 'Blocked users updated');
    } catch (error) {
      showError(error, 'Unable to unblock this profile.');
    } finally {
      setUnblockingUid(null);
    }
  }

  const handleProfileSubmit = handleSubmit(async (values) => {
    const nameLocked = fullNameIsIdentityLocked(
      profileQuery.data?.verification,
    );
    const savedBio = profileQuery.data?.bio?.trim() || null;
    const nextBio = values.bio?.trim() || null;
    const bioChanged = nextBio !== savedBio;
    const shouldOmitUnchangedLongBio =
      !bioChanged && nextBio != null && nextBio.length > MAX_PROFILE_BIO_LENGTH;
    const parsed = profileSchema.safeParse({
      ...values,
      bio: shouldOmitUnchangedLongBio ? null : nextBio,
      fullName: values.fullName.trim(),
      location: values.location?.trim() || null,
    });

    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path.join('.');
        const profileField = field.startsWith('gender') ? 'gender' : field;

        if (
          profileField === 'bio' ||
          profileField === 'fullName' ||
          profileField === 'gender' ||
          profileField === 'location'
        ) {
          setError(profileField as keyof ProfileValues, {
            message: issue.message,
          });
        }
      });
      return;
    }

    try {
      const updateInput: UpdateProfileInput = { ...parsed.data };

      if (nameLocked) {
        delete updateInput.fullName;
      }

      if (shouldOmitUnchangedLongBio || !dirtyFields.bio) {
        delete updateInput.bio;
      }

      const result = await updateProfile.mutateAsync(updateInput);
      await profileQuery.refetch();
      trackEvent('profile_update', {
        has_bio: Boolean(nextBio),
        has_full_name: Boolean(parsed.data.fullName),
        has_location: Boolean(parsed.data.location),
        gender_value: parsed.data.gender.value,
      });
      showSuccess(
        result === 'queued'
          ? 'Your profile changes will sync when your connection is back.'
          : 'Your profile changes were saved.',
        result === 'queued' ? 'Profile queued' : 'Profile saved',
      );
    } catch (caughtError) {
      showError(
        caughtError,
        'Unable to update your profile. Please try again.',
      );
    }
  });

  async function handleShareProfileLink() {
    const username = profileQuery.data?.username;

    if (!username) {
      return;
    }

    const url = `https://frienzzi.com/profile/${username}`;

    try {
      if (Platform.OS === 'web') {
        await copyTextToClipboard(url);
        showSuccess('Profile link copied.', 'Ready to share');
        return;
      }

      await Share.share({
        message: url,
        url,
      });
    } catch (error) {
      if (isShareCancelError(error)) {
        return;
      }

      showError(error, 'Unable to share profile link.');
    }
  }

  async function handleCopyProfileLink() {
    const username = profileQuery.data?.username;

    if (!username) {
      return;
    }

    const url = `https://frienzzi.com/profile/${username}`;

    try {
      await copyTextToClipboard(url);
      showSuccess('Profile link copied.', 'Copied');
    } catch (error) {
      showError(error, 'Unable to copy profile link.');
    }
  }

  function handleReportProblem() {
    const profile = profileQuery.data;

    setFeedbackName(profile?.fullName ?? user?.displayName ?? '');
    setFeedbackEmail(user?.email ?? '');
    setFeedbackDescription('');
    setFeedbackError(null);
    setFeedbackVisible(true);
    trackEvent('diagnostics_feedback_open');
  }

  async function handleEmailSupport() {
    const mailURL = `mailto:${supportEmail}`;

    try {
      if (await Linking.canOpenURL(mailURL)) {
        await Linking.openURL(mailURL);
        trackEvent('support_email_open');
        return;
      }

      throw new Error('Email app is not available.');
    } catch (error) {
      try {
        await copyTextToClipboard(supportEmail);
        showSuccess('Support email copied.', 'Copied');
        trackEvent('support_email_copy');
      } catch {
        showError(error, 'Unable to open support email.');
      }
    }
  }

  async function handleSubmitFeedback() {
    const description = feedbackDescription.trim();

    if (!description) {
      setFeedbackError('Add a short description before sending.');
      return;
    }

    setSubmittingFeedback(true);
    setFeedbackError(null);

    try {
      await submitSentryFeedback({
        email: feedbackEmail,
        message: description,
        name: feedbackName,
      });
      trackEvent('diagnostics_feedback_submit');
      setFeedbackVisible(false);
      setFeedbackDescription('');
      showSuccess('Thanks. Your report was sent.', 'Report sent');
    } catch (caughtError) {
      showError(caughtError, 'Unable to send your report right now.');
    } finally {
      setSubmittingFeedback(false);
    }
  }

  const verification = parseVerifiedAccount(profileQuery.data?.verification);
  const verifiedBadgeComplete = verification?.badge.verified === true;
  const currentVerifiedPhone = verification?.phone.phoneNumber ?? null;
  const currentVerifiedPhoneLabel =
    verification?.phone.status === 'verified' && verification.phone.last4
      ? `Verified ending in ${verification.phone.last4}`
      : 'No verified phone on file';
  const normalizedPhoneNumber = normalizeUsPhoneNumber(phoneNumber);
  const phoneChangeMatchesCurrentPhone = phoneNumberMatchesStoredPhone(
    phoneNumber,
    currentVerifiedPhone,
  );
  const phoneChangeBusy = sendingPhoneCode || confirmingPhoneCode;
  const blockedUsers =
    blockedUsersQuery.data?.pages.flatMap((page) => page.blockedUsers) ?? [];
  const blockedUsersInitialLoading =
    blockedUsersQuery.isLoading ||
    (blockedUsersQuery.isFetching && blockedUsers.length === 0);
  const blockedUsersEmptyMessage = blockedSearchQuery
    ? 'No blocked users match that search.'
    : 'You have not blocked anyone.';

  return (
    <Screen title="Settings" subtitle="Preferences and account actions.">
      <Modal
        animationType="fade"
        transparent
        visible={feedbackVisible}
        onRequestClose={() => {
          if (!submittingFeedback) {
            setFeedbackVisible(false);
          }
        }}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.feedbackModalRoot}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close report problem form"
            disabled={submittingFeedback}
            style={styles.feedbackModalBackdrop}
            onPress={() => setFeedbackVisible(false)}
          />
          <View style={styles.feedbackModalCard}>
            <View style={styles.feedbackModalHeader}>
              <Text style={styles.feedbackModalTitle}>Report a problem</Text>
              <Pressable
                accessibilityRole="button"
                disabled={submittingFeedback}
                hitSlop={10}
                style={styles.feedbackModalClose}
                onPress={() => setFeedbackVisible(false)}
              >
                <Text style={styles.feedbackModalCloseText}>x</Text>
              </Pressable>
            </View>
            <Text style={styles.settingDescription}>
              We will include safe technical details to help debug the issue.
            </Text>
            <View style={styles.feedbackFields}>
              <View style={styles.field}>
                <Text style={styles.label}>Name</Text>
                <TextInput
                  autoCapitalize="words"
                  autoComplete="name"
                  editable={!submittingFeedback}
                  placeholder="Your name"
                  placeholderTextColor={colors.mutedText}
                  selectionColor={colors.primary}
                  style={styles.feedbackInput}
                  textContentType="name"
                  value={feedbackName}
                  onChangeText={setFeedbackName}
                />
              </View>
              <View style={styles.field}>
                <Text style={styles.label}>Email</Text>
                <TextInput
                  autoCapitalize="none"
                  autoComplete="email"
                  editable={!submittingFeedback}
                  inputMode="email"
                  keyboardType="email-address"
                  placeholder="you@example.com"
                  placeholderTextColor={colors.mutedText}
                  selectionColor={colors.primary}
                  style={styles.feedbackInput}
                  textContentType="emailAddress"
                  value={feedbackEmail}
                  onChangeText={setFeedbackEmail}
                />
              </View>
              <View style={styles.field}>
                <Text style={styles.label}>What happened?</Text>
                <TextInput
                  editable={!submittingFeedback}
                  multiline
                  placeholder="Tell us what broke or felt off."
                  placeholderTextColor={colors.mutedText}
                  selectionColor={colors.primary}
                  style={[
                    styles.feedbackInput,
                    styles.feedbackDescriptionInput,
                  ]}
                  textAlignVertical="top"
                  value={feedbackDescription}
                  onChangeText={(value) => {
                    setFeedbackDescription(value);
                    if (feedbackError) {
                      setFeedbackError(null);
                    }
                  }}
                />
                {feedbackError ? (
                  <Text style={styles.error}>{feedbackError}</Text>
                ) : null}
              </View>
            </View>
            <View style={styles.feedbackModalActions}>
              <Button
                title="Cancel"
                variant="secondary"
                disabled={submittingFeedback}
                style={styles.feedbackModalButton}
                onPress={() => setFeedbackVisible(false)}
              />
              <Button
                title="Send report"
                loading={submittingFeedback}
                disabled={!feedbackDescription.trim()}
                style={styles.feedbackModalButton}
                onPress={handleSubmitFeedback}
              />
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
      <Modal
        animationType="fade"
        transparent
        visible={feedRankingVisible}
        onRequestClose={() => setFeedRankingVisible(false)}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close feed ranking explanation"
          style={styles.feedbackModalBackdrop}
          onPress={() => setFeedRankingVisible(false)}
        />
        <View style={styles.feedRankingModalRoot} pointerEvents="box-none">
          <View style={styles.feedbackModalCard}>
            <View style={styles.feedbackModalHeader}>
              <Text style={styles.feedbackModalTitle}>
                How feeds are ranked
              </Text>
              <Pressable
                accessibilityRole="button"
                hitSlop={10}
                style={styles.feedbackModalClose}
                onPress={() => setFeedRankingVisible(false)}
              >
                <Text style={styles.feedbackModalCloseText}>x</Text>
              </Pressable>
            </View>
            <View style={styles.rankingInfoBody}>
              <Text style={styles.settingDescription}>
                Ranked feeds keep recent posts important, then add small boosts
                for people you follow, mutual friends, active conversations,
                Garage posts, reactions, comments, and posts you choose to open.
              </Text>
              <Text style={styles.settingDescription}>
                AI helps Frienzzi label post topics and media from post text,
                link previews, Garage cards, and generated thumbnails. AI does
                not directly order your feed.
              </Text>
              <Text style={styles.settingDescription}>
                Hide removes a post from your ranked feed. Show me less like
                this lowers similar posts. Latest always shows chronological
                posts without ranked ordering.
              </Text>
            </View>
            <Button title="Done" onPress={() => setFeedRankingVisible(false)} />
          </View>
        </View>
      </Modal>
      <Modal
        animationType="fade"
        transparent
        visible={blockedUsersVisible}
        onRequestClose={() => setBlockedUsersVisible(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.feedbackModalRoot}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close blocked users"
            style={styles.feedbackModalBackdrop}
            onPress={() => setBlockedUsersVisible(false)}
          />
          <View style={[styles.feedbackModalCard, styles.blockedModalCard]}>
            <View style={styles.feedbackModalHeader}>
              <Text style={styles.feedbackModalTitle}>Blocked users</Text>
              <Pressable
                accessibilityRole="button"
                hitSlop={10}
                style={styles.feedbackModalClose}
                onPress={() => setBlockedUsersVisible(false)}
              >
                <Text style={styles.feedbackModalCloseText}>x</Text>
              </Pressable>
            </View>
            <TextInput
              autoCapitalize="none"
              autoCorrect={false}
              clearButtonMode="while-editing"
              placeholder="Search blocked users"
              placeholderTextColor={colors.mutedText}
              selectionColor={colors.primary}
              style={styles.feedbackInput}
              value={blockedSearchInput}
              onChangeText={setBlockedSearchInput}
            />
            {blockedUsersQuery.isError ? (
              <ErrorView
                message={blockedUsersQuery.error.message}
                onRetry={() => {
                  void blockedUsersQuery.refetch();
                }}
              />
            ) : null}
            {blockedUsersInitialLoading ? (
              <Text style={styles.settingDescription}>
                Loading blocked users...
              </Text>
            ) : null}
            {!blockedUsersInitialLoading &&
            !blockedUsersQuery.isError &&
            blockedUsers.length === 0 ? (
              <Text style={styles.settingDescription}>
                {blockedUsersEmptyMessage}
              </Text>
            ) : null}
            {blockedUsers.length > 0 ? (
              <ScrollView
                style={styles.blockedModalList}
                contentContainerStyle={styles.blockedUsersList}
                keyboardShouldPersistTaps="handled"
              >
                {blockedUsers.map((profile) => (
                  <View key={profile.uid} style={styles.blockedUserRow}>
                    <View style={styles.blockedUserText}>
                      <Text style={styles.blockedUserName}>
                        {profile.fullName}
                      </Text>
                      {profile.username ? (
                        <Text style={styles.settingDescription}>
                          @{profile.username}
                        </Text>
                      ) : null}
                    </View>
                    <Button
                      title="Unblock"
                      variant="secondary"
                      loading={unblockingUid === profile.uid}
                      disabled={Boolean(unblockingUid)}
                      onPress={() => {
                        void handleUnblockUser(profile);
                      }}
                    />
                  </View>
                ))}
                {blockedUsersQuery.hasNextPage ? (
                  <Button
                    title="Load more"
                    variant="secondary"
                    loading={blockedUsersQuery.isFetchingNextPage}
                    disabled={blockedUsersQuery.isFetchingNextPage}
                    onPress={() => {
                      void blockedUsersQuery.fetchNextPage();
                    }}
                  />
                ) : null}
              </ScrollView>
            ) : null}
          </View>
        </KeyboardAvoidingView>
      </Modal>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Edit profile</Text>
          <Text style={styles.settingDescription}>
            Update the profile information people see on Frienzzi.
          </Text>
        </View>
        {profileQuery.isLoading ? (
          <Text style={styles.settingDescription}>Loading profile...</Text>
        ) : null}
        {profileQuery.isError ? (
          <ErrorView
            message={profileQuery.error.message}
            onRetry={() => {
              void profileQuery.refetch();
            }}
          />
        ) : null}
        {profileQuery.data ? (
          <View style={styles.profileForm}>
            {profileQuery.data.username ? (
              <View style={styles.usernameCard}>
                <Text style={styles.usernameHandle}>
                  @{profileQuery.data.username}
                </Text>
                <Text style={styles.settingDescription}>
                  https://frienzzi.com/profile/{profileQuery.data.username}
                </Text>
                <View style={styles.usernameActions}>
                  <Button
                    title="Copy link"
                    variant="secondary"
                    style={styles.usernameActionButton}
                    onPress={() => {
                      void handleCopyProfileLink();
                    }}
                  />
                  <Button
                    title="Share link"
                    variant="secondary"
                    style={styles.usernameActionButton}
                    onPress={() => {
                      void handleShareProfileLink();
                    }}
                  />
                </View>
              </View>
            ) : null}
            <Controller
              control={control}
              name="fullName"
              render={({ field: { onBlur, onChange, value } }) => (
                <Input
                  label="Full name"
                  autoCapitalize="words"
                  editable={
                    !fullNameIsIdentityLocked(profileQuery.data?.verification)
                  }
                  onBlur={onBlur}
                  onChangeText={onChange}
                  placeholder="Add your full name"
                  textContentType="name"
                  value={value}
                  error={errors.fullName?.message}
                />
              )}
            />
            {fullNameIsIdentityLocked(profileQuery.data.verification) ? (
              <Text style={styles.settingDescription}>
                Your full name is locked because it was verified from your
                government ID.
              </Text>
            ) : null}
            <Controller
              control={control}
              name="bio"
              render={({ field: { onBlur, onChange, value } }) => (
                <View style={styles.bioField}>
                  <Input
                    label="Bio"
                    maxLength={MAX_PROFILE_BIO_LENGTH}
                    multiline
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Share a little about yourself"
                    style={styles.bioInput}
                    textAlignVertical="top"
                    value={value ?? ''}
                    error={errors.bio?.message}
                  />
                  <Text
                    style={[
                      styles.characterCount,
                      (value ?? '').length > MAX_PROFILE_BIO_LENGTH
                        ? styles.characterCountOver
                        : null,
                    ]}
                  >
                    {(value ?? '').length}/{MAX_PROFILE_BIO_LENGTH}
                  </Text>
                  {(value ?? '').length > MAX_PROFILE_BIO_LENGTH ? (
                    <Text style={styles.settingDescription}>
                      You can keep this existing bio, but future bio edits need
                      to be {MAX_PROFILE_BIO_LENGTH} characters or fewer.
                    </Text>
                  ) : null}
                </View>
              )}
            />
            <Controller
              control={control}
              name="gender"
              render={({ field: { onChange, value } }) => (
                <View style={styles.field}>
                  <Text style={styles.label}>Gender</Text>
                  <View style={styles.optionGrid}>
                    {genderOptions.map((option) => {
                      const selected = value?.value === option.value;

                      return (
                        <Pressable
                          key={option.value}
                          accessibilityRole="button"
                          accessibilityState={{ selected }}
                          onPress={() => {
                            onChange(genderValueToFormValue(option.value));
                          }}
                          style={[
                            styles.option,
                            selected ? styles.optionSelected : null,
                          ]}
                        >
                          <Text
                            style={[
                              styles.optionText,
                              selected ? styles.optionTextSelected : null,
                            ]}
                          >
                            {option.label}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                  {errors.gender?.message ? (
                    <Text style={styles.error}>{errors.gender.message}</Text>
                  ) : null}
                </View>
              )}
            />
            <Controller
              control={control}
              name="location"
              render={({ field: { onBlur, onChange, value } }) => (
                <CityStateAutocomplete
                  label="Location"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  placeholder="Start typing your city"
                  value={value ?? ''}
                  error={errors.location?.message}
                />
              )}
            />
            <Button
              title="Save profile"
              loading={isSubmitting || updateProfile.isPending}
              onPress={handleProfileSubmit}
            />
          </View>
        ) : null}
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Connected accounts</Text>
          <Text style={styles.settingDescription}>
            Connect social accounts so you can sign in with supported providers.
          </Text>
        </View>
        <View style={styles.connectedAccountActions}>
          {googleAuth.available || googleConnected ? (
            <GoogleButton
              title={googleConnected ? 'Google connected' : 'Connect Google'}
              disabled={googleConnected || googleAuth.disabled}
              loading={connectingGoogle}
              onPress={handleConnectGoogle}
            />
          ) : null}
          <FacebookButton
            title={
              profileQuery.data?.authProviders?.facebook
                ? 'Facebook connected'
                : 'Connect Facebook'
            }
            disabled={
              Boolean(profileQuery.data?.authProviders?.facebook) ||
              facebookAuth.disabled
            }
            loading={connectingFacebook}
            onPress={handleConnectFacebook}
          />
        </View>
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Verified account</Text>
          <Text style={styles.settingDescription}>
            Use the guided verification flow to pay the processing fee, confirm
            your email and cell phone, and complete Stripe Identity.
          </Text>
        </View>
        <View style={styles.verificationRows}>
          <Text style={styles.verificationRow}>
            Status: {verifiedBadgeComplete ? 'Badge active' : 'Not verified'}
          </Text>
          <Text style={styles.settingDescription}>
            The verified account flow is optional. The $10 plus applicable tax
            fee covers verification processing and can be refunded before
            identity verification starts, for duplicate charges, platform
            errors, or where required by law.
          </Text>
        </View>
        <Button
          title={
            verifiedBadgeComplete ? 'View verified status' : 'Get verified'
          }
          onPress={() => router.push('/verify-account')}
        />
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Account email</Text>
          <Text style={styles.settingDescription}>
            Current email: {user?.email ?? 'Not available'}
            {user?.emailVerified ? ' (verified)' : ' (not verified)'}
          </Text>
          <Text style={styles.settingDescription}>
            Enter your current password and a new email address. Frienzzi will
            update your account after you confirm the link sent to the new
            address.
          </Text>
        </View>
        <View style={styles.emailChangeForm}>
          <Controller
            control={emailChangeControl}
            name="newEmail"
            render={({ field: { onBlur, onChange, value } }) => (
              <Input
                label="New email"
                autoCapitalize="none"
                autoComplete="email"
                keyboardType="email-address"
                onBlur={onBlur}
                onChangeText={onChange}
                textContentType="emailAddress"
                value={value}
                error={emailChangeErrors.newEmail?.message}
              />
            )}
          />
          <Controller
            control={emailChangeControl}
            name="currentPassword"
            render={({ field: { onBlur, onChange, value } }) => (
              <Input
                label="Current password"
                autoComplete="current-password"
                onBlur={onBlur}
                onChangeText={onChange}
                secureTextEntry
                textContentType="password"
                value={value}
                error={emailChangeErrors.currentPassword?.message}
              />
            )}
          />
          <Button
            title="Send email change confirmation"
            loading={isSubmittingEmailChange}
            onPress={handleEmailChange}
          />
        </View>
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Account phone</Text>
          <Text style={styles.settingDescription}>
            Current phone: {currentVerifiedPhoneLabel}
          </Text>
          <Text style={styles.settingDescription}>
            Enter a new US cell phone number. Frienzzi will not update your
            account phone until you confirm the SMS code sent to the new number.
          </Text>
        </View>
        <View style={styles.emailChangeForm}>
          <Input
            autoComplete="tel"
            editable={!phoneChangeBusy}
            keyboardType="phone-pad"
            label="New phone number"
            onChangeText={handlePhoneNumberChange}
            placeholder="(303) 555-0123"
            textContentType="telephoneNumber"
            value={phoneNumber}
          />
          <Text style={styles.settingDescription}>
            Spaces, dashes, parentheses, and +1 are fine. Only verified US cell
            phone numbers can be saved.
          </Text>
          {Platform.OS === 'web' ? (
            <View
              nativeID="frienzzi-phone-verification-recaptcha"
              style={styles.recaptchaContainer}
            />
          ) : null}
          <Button
            disabled={
              phoneChangeBusy ||
              !normalizedPhoneNumber ||
              phoneChangeMatchesCurrentPhone
            }
            loading={sendingPhoneCode}
            onPress={handleSendPhoneChangeCode}
            title={
              Platform.OS === 'web'
                ? 'Send phone verification code'
                : 'Verify phone in browser'
            }
          />
          {phoneChangeMatchesCurrentPhone ? (
            <Text style={styles.settingDescription}>
              Enter a different number to request a phone change.
            </Text>
          ) : null}
          {phoneVerificationId ? (
            <Button
              title="Enter verification code"
              variant="secondary"
              onPress={() => setPhoneCodeModalVisible(true)}
            />
          ) : null}
        </View>
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Password</Text>
          <Text style={styles.settingDescription}>
            Send a Firebase password reset email to your account email address.
          </Text>
        </View>
        <Button
          title="Send password reset email"
          variant="secondary"
          onPress={handlePasswordReset}
        />
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Writing assist</Text>
          <Text style={styles.settingDescription}>
            Automatically clean up spelling, grammar, and punctuation in posts
            and comments while preserving casual internet slang like lol, omg,
            and rn.
          </Text>
        </View>
        <View style={styles.preferenceRow}>
          <View style={styles.preferenceText}>
            <Text style={styles.preferenceTitle}>Auto-correct my writing</Text>
            <Text style={styles.settingDescription}>
              Draft text is checked right before posting when this is on.
            </Text>
          </View>
          <Switch
            disabled={savingWritingAssist}
            ios_backgroundColor={colors.border}
            thumbColor={colors.surface}
            trackColor={{
              false: colors.border,
              true: colors.primary,
            }}
            value={writingAssist.autoCorrectEnabled}
            onValueChange={(value) => {
              void handleWritingAssistChange(value);
            }}
          />
        </View>
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Notifications</Text>
          <Text style={styles.settingDescription}>
            Choose which activity creates in-app notifications and email
            notifications. Push notifications can be enabled after Frienzzi is
            installed as a PWA. Post reaction and comment reaction emails are
            grouped into hourly digests. Frienzzi update emails are sent daily
            only when public changelog entries were published.
          </Text>
        </View>
        <WebPushSettingsPanel />
        <View style={styles.notificationHeader}>
          <Text style={styles.notificationHeaderLabel}>Activity</Text>
          <Text style={styles.notificationColumnLabel}>Email</Text>
          <Text style={styles.notificationColumnLabel}>In-app</Text>
          <Text style={styles.notificationColumnLabel}>Push</Text>
        </View>
        {notificationRows.map((row) => (
          <View key={row.key} style={styles.notificationRow}>
            <Text style={styles.notificationLabel}>{row.label}</Text>
            <View style={styles.notificationSwitchColumn}>
              <Switch
                disabled={savingNotificationSetting !== null}
                ios_backgroundColor={colors.border}
                thumbColor={colors.surface}
                trackColor={{
                  false: colors.border,
                  true: colors.primary,
                }}
                value={notificationSettings.email[row.key]}
                onValueChange={(value) => {
                  void handleNotificationSettingChange({
                    channel: 'email',
                    topic: row.key,
                    value,
                  });
                }}
              />
            </View>
            <View style={styles.notificationSwitchColumn}>
              <Switch
                disabled={savingNotificationSetting !== null}
                ios_backgroundColor={colors.border}
                thumbColor={colors.surface}
                trackColor={{
                  false: colors.border,
                  true: colors.primary,
                }}
                value={notificationSettings.inApp[row.key]}
                onValueChange={(value) => {
                  void handleNotificationSettingChange({
                    channel: 'inApp',
                    topic: row.key,
                    value,
                  });
                }}
              />
            </View>
            <View style={styles.notificationSwitchColumn}>
              <Switch
                disabled={savingNotificationSetting !== null}
                ios_backgroundColor={colors.border}
                thumbColor={colors.surface}
                trackColor={{
                  false: colors.border,
                  true: colors.primary,
                }}
                value={notificationSettings.push[row.key]}
                onValueChange={(value) => {
                  void handleNotificationSettingChange({
                    channel: 'push',
                    topic: row.key,
                    value,
                  });
                }}
              />
            </View>
          </View>
        ))}
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Feeds</Text>
          <Text style={styles.settingDescription}>
            Learn what affects Ranked feeds and how Frienzzi uses AI labels.
          </Text>
        </View>
        <Button
          title="How feeds are ranked"
          variant="secondary"
          onPress={() => setFeedRankingVisible(true)}
        />
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Blocked users</Text>
          <Text style={styles.settingDescription}>
            Blocked people cannot see or interact with you on Frienzzi, and you
            will not see them in your feeds, searches, mentions, or
            notifications.
          </Text>
        </View>
        <Button
          title="Manage blocked users"
          variant="secondary"
          onPress={() => setBlockedUsersVisible(true)}
        />
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Help and diagnostics</Text>
          <Text style={styles.settingDescription}>
            Report a problem with technical details that help Frienzzi debug the
            app faster.
          </Text>
        </View>
        <View style={styles.diagnosticsActions}>
          <Button title="Report a problem" onPress={handleReportProblem} />
          <Button
            title="Email support"
            variant="secondary"
            onPress={() => {
              void handleEmailSupport();
            }}
          />
        </View>
      </View>
      <View style={styles.panel}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Legal agreements</Text>
          <Text style={styles.settingDescription}>
            Current terms version {CURRENT_TERMS_VERSION}. Current privacy
            version {CURRENT_PRIVACY_VERSION}.
          </Text>
        </View>
        <View style={styles.legalLinks}>
          <Link href="/terms" style={styles.link}>
            Terms of Service
          </Link>
          <Link href="/privacy" style={styles.link}>
            Privacy Policy
          </Link>
        </View>
        <Text style={styles.settingDescription}>
          Accepted terms:{' '}
          {profileQuery.data?.legal?.termsVersion ?? 'Not recorded'}
        </Text>
        <Text style={styles.settingDescription}>
          Accepted privacy:{' '}
          {profileQuery.data?.legal?.privacyVersion ?? 'Not recorded'}
        </Text>
        <Text style={styles.settingDescription}>
          Terms document:{' '}
          {profileQuery.data?.legal?.termsDocumentId ?? 'Not recorded'}
        </Text>
        <Text style={styles.settingDescription}>
          Privacy document:{' '}
          {profileQuery.data?.legal?.privacyDocumentId ?? 'Not recorded'}
        </Text>
      </View>
      <View style={[styles.panel, styles.dangerPanel]}>
        <View style={styles.legalHeader}>
          <Text style={styles.settingTitle}>Delete account</Text>
          <Text style={styles.settingDescription}>
            Schedule your Frienzzi account and data for permanent deletion. You
            can cancel for 30 days before the purge runs.
          </Text>
        </View>
        {confirmingDeletion ? (
          <View style={styles.confirmPanel}>
            <Text style={styles.confirmText}>
              This immediately blocks normal app access and schedules permanent
              deletion after the 30-day recovery window.
            </Text>
            <View style={styles.confirmActions}>
              <Button
                title="Cancel"
                variant="secondary"
                disabled={requestingDeletion}
                onPress={() => setConfirmingDeletion(false)}
              />
              <Button
                title="Schedule deletion"
                variant="danger"
                loading={requestingDeletion}
                onPress={handleRequestDeletion}
              />
            </View>
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            style={styles.deleteLink}
            onPress={() => setConfirmingDeletion(true)}
          >
            <Text style={styles.deleteLinkText}>Delete my account</Text>
          </Pressable>
        )}
      </View>
      <Button
        title="Sign out"
        variant="danger"
        loading={signingOut}
        onPress={handleSignOut}
      />
      <PhoneVerificationCodeModal
        code={phoneVerificationCode}
        description="Enter the SMS code we sent to your new phone number. Your account phone will not change until this code is verified."
        loading={confirmingPhoneCode}
        title="Confirm phone change"
        visible={Boolean(phoneVerificationId) && phoneCodeModalVisible}
        onChangeCode={setPhoneVerificationCode}
        onClose={() => setPhoneCodeModalVisible(false)}
        onConfirm={handleConfirmPhoneChangeCode}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  panel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    padding: spacing.lg,
  },
  settingTitle: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '800',
  },
  settingDescription: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  profileForm: {
    gap: spacing.lg,
  },
  bioField: {
    gap: spacing.xs,
  },
  bioInput: {
    minHeight: 112,
  },
  characterCount: {
    alignSelf: 'flex-end',
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
  },
  characterCountOver: {
    color: colors.danger,
  },
  usernameCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  usernameHandle: {
    color: colors.primary,
    fontSize: 18,
    fontWeight: '900',
  },
  usernameActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  usernameActionButton: {
    flex: 1,
  },
  field: {
    gap: spacing.sm,
  },
  label: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
  },
  optionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  option: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  optionSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  optionText: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '800',
  },
  optionTextSelected: {
    color: colors.surface,
  },
  error: {
    color: colors.danger,
    fontSize: 13,
  },
  legalHeader: {
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  legalLinks: {
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  connectedAccountActions: {
    gap: spacing.md,
  },
  emailChangeForm: {
    gap: spacing.md,
  },
  recaptchaContainer: {
    alignSelf: 'flex-start',
    minHeight: 78,
    minWidth: 304,
  },
  verificationRows: {
    gap: spacing.xs,
  },
  verificationRow: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  notificationHeader: {
    alignItems: 'center',
    borderBottomColor: colors.border,
    borderBottomWidth: 1,
    flexDirection: 'row',
    paddingRight: spacing.xs,
    paddingBottom: spacing.sm,
  },
  notificationHeaderLabel: {
    color: colors.mutedText,
    flex: 1,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  notificationColumnLabel: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '900',
    textAlign: 'center',
    textTransform: 'uppercase',
    width: 72,
  },
  notificationRow: {
    alignItems: 'center',
    borderBottomColor: colors.border,
    borderBottomWidth: 1,
    flexDirection: 'row',
    paddingRight: spacing.xs,
    paddingVertical: spacing.md,
  },
  preferenceRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  preferenceText: {
    flex: 1,
    gap: spacing.xs,
  },
  preferenceTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '800',
  },
  notificationLabel: {
    color: colors.text,
    flex: 1,
    fontSize: 15,
    fontWeight: '800',
  },
  notificationSwitchColumn: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 72,
  },
  link: {
    color: colors.primary,
    fontSize: 15,
    fontWeight: '800',
  },
  dangerPanel: {
    borderColor: colors.danger,
  },
  confirmPanel: {
    gap: spacing.md,
  },
  confirmText: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 20,
  },
  confirmActions: {
    gap: spacing.sm,
  },
  deleteLink: {
    alignSelf: 'flex-start',
    paddingVertical: spacing.xs,
  },
  deleteLinkText: {
    color: colors.danger,
    fontSize: 15,
    fontWeight: '900',
  },
  diagnosticsActions: {
    gap: spacing.sm,
  },
  blockedModalCard: {
    maxHeight: '82%',
  },
  blockedModalList: {
    maxHeight: 420,
  },
  blockedUsersList: {
    gap: spacing.sm,
  },
  blockedUserRow: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    padding: spacing.md,
  },
  blockedUserText: {
    flex: 1,
    gap: spacing.xs,
  },
  blockedUserName: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '800',
  },
  feedbackModalRoot: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  feedRankingModalRoot: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  feedbackModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(20, 13, 45, 0.44)',
  },
  feedbackModalCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 520,
    padding: spacing.lg,
    width: '100%',
  },
  feedbackModalHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  feedbackModalTitle: {
    color: colors.text,
    flex: 1,
    fontSize: 18,
    fontWeight: '900',
  },
  feedbackModalClose: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 18,
    borderWidth: 1,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  feedbackModalCloseText: {
    color: colors.mutedText,
    fontSize: 18,
    fontWeight: '900',
    lineHeight: 20,
  },
  feedbackFields: {
    gap: spacing.md,
  },
  rankingInfoBody: {
    gap: spacing.sm,
  },
  feedbackInput: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    color: colors.text,
    fontSize: 16,
    minHeight: 46,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  feedbackDescriptionInput: {
    minHeight: 120,
    paddingTop: spacing.md,
  },
  feedbackModalActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  feedbackModalButton: {
    flex: 1,
  },
});
